"use client";

import { useState, useEffect } from "react";
import { useSearchParams } from "next/navigation";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "@/i18n";
import { SectionWrapper, SectionEyebrow, SectionHeadline } from "@/components/ui/SectionWrapper";
import { FadeIn } from "@/components/ui/AnimatedSection";

export function Newsletter() {
    const [email, setEmail] = useState("");
    const [status, setStatus] = useState<"idle" | "loading" | "success" | "error" | "confirmed" | "unsubscribed">("idle");
    const [errorMessage, setErrorMessage] = useState("");
    const searchParams = useSearchParams();
    const { t } = useTranslation();

    useEffect(() => {
        const newsletterParam = searchParams.get("newsletter");
        if (newsletterParam === "confirmed") {
            setStatus("confirmed");
            document.getElementById("newsletter")?.scrollIntoView({ behavior: "smooth" });
        } else if (newsletterParam === "unsubscribed") {
            setStatus("unsubscribed");
            document.getElementById("newsletter")?.scrollIntoView({ behavior: "smooth" });
        }
    }, [searchParams]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
            setErrorMessage(t.newsletter.emailError);
            setStatus("error");
            return;
        }

        setStatus("loading");
        setErrorMessage("");

        try {
            const res = await fetch("/api/newsletter", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ email }),
            });

            if (!res.ok) {
                const data = await res.json().catch(() => null);
                const safeMessage =
                    res.status < 500 && typeof data?.error === "string"
                        ? data.error
                        : t.newsletter.genericError;

                throw new Error(safeMessage);
            }

            setStatus("success");
            setEmail("");
        } catch (err) {
            setErrorMessage(
                err instanceof Error && err.message
                    ? err.message
                    : t.newsletter.genericError
            );
            setStatus("error");
        }
    };

    return (
        <SectionWrapper id="newsletter" narrow className="py-(--section-y-lg)">
            <div className="text-center">
                <FadeIn>
                    <SectionEyebrow className="text-center">
                        {t.newsletter.eyebrow}
                    </SectionEyebrow>
                    <SectionHeadline className="text-center">
                        {t.newsletter.headline}
                    </SectionHeadline>
                    <p className="text-text-secondary leading-relaxed mb-10 max-w-lg mx-auto">
                        {t.newsletter.description}
                    </p>
                </FadeIn>

                <FadeIn delay={0.2}>
                    <AnimatePresence mode="wait">
                        {status === "confirmed" ? (
                            <motion.div
                                key="confirmed"
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -10 }}
                                className="flex flex-col items-center gap-3"
                            >
                                <div className="w-12 h-12 rounded-full bg-terra/10 border border-terra/30 flex items-center justify-center mb-2">
                                    <svg viewBox="0 0 24 24" fill="none" className="w-6 h-6 text-terra" aria-hidden="true">
                                        <path d="M5 12l5 5L20 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                </div>
                                <p className="text-lg font-medium text-text">
                                    {t.newsletter.confirmedMessage}
                                </p>
                                <p className="text-sm text-text-secondary max-w-sm mx-auto">
                                    {t.newsletter.confirmedDetail}
                                </p>
                            </motion.div>
                        ) : status === "unsubscribed" ? (
                            <motion.div
                                key="unsubscribed"
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -10 }}
                                className="flex flex-col items-center gap-3"
                            >
                                <div className="w-12 h-12 rounded-full bg-border border border-border-light flex items-center justify-center mb-2">
                                    <svg viewBox="0 0 24 24" fill="none" className="w-6 h-6 text-text-secondary" aria-hidden="true">
                                        <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                                    </svg>
                                </div>
                                <p className="text-lg font-medium text-text">
                                    {t.newsletter.unsubscribedMessage}
                                </p>
                                <p className="text-sm text-text-secondary max-w-sm mx-auto">
                                    {t.newsletter.unsubscribedDetail}
                                </p>
                            </motion.div>
                        ) : status === "success" ? (
                            <motion.div
                                key="success"
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -10 }}
                                className="flex flex-col items-center gap-3"
                            >
                                <div className="w-12 h-12 rounded-full bg-mars/10 border border-mars/30 flex items-center justify-center mb-2">
                                    <svg viewBox="0 0 24 24" fill="none" className="w-6 h-6 text-mars" aria-hidden="true">
                                        <path d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                </div>
                                <p className="text-lg font-medium text-text">
                                    {t.newsletter.successMessage}
                                </p>
                                <p className="text-sm text-text-secondary max-w-sm mx-auto">
                                    {t.newsletter.successDetail}
                                </p>
                                <p className="text-xs text-text-muted mt-1 max-w-xs mx-auto">
                                    {t.newsletter.spamNote}
                                </p>
                            </motion.div>
                        ) : (
                            <motion.form
                                key="form"
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -10 }}
                                onSubmit={handleSubmit}
                                className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto"
                            >
                                {/* Honeypot for anti-spam */}
                                <input
                                    type="text"
                                    name="website"
                                    tabIndex={-1}
                                    autoComplete="off"
                                    className="absolute opacity-0 pointer-events-none h-0 w-0"
                                    aria-hidden="true"
                                />

                                <input
                                    type="email"
                                    value={email}
                                    onChange={(e) => {
                                        setEmail(e.target.value);
                                        if (status === "error") setStatus("idle");
                                    }}
                                    placeholder={t.newsletter.placeholder}
                                    className="flex-1 px-4 py-3 rounded-lg bg-surface border border-border text-text text-sm placeholder:text-text-muted focus:border-mars focus:outline-none transition-colors"
                                    required
                                    aria-label="Email address"
                                />
                                <button
                                    type="submit"
                                    disabled={status === "loading"}
                                    className="px-6 py-3 rounded-lg bg-mars text-white text-sm font-medium hover:bg-mars-light transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
                                >
                                    {status === "loading" ? t.newsletter.subscribing : t.newsletter.cta}
                                </button>
                            </motion.form>
                        )}
                    </AnimatePresence>

                    {/* Error */}
                    <AnimatePresence>
                        {status === "error" && errorMessage && (
                            <motion.p
                                initial={{ opacity: 0, y: 5 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0 }}
                                className="text-sm text-red-400 mt-3"
                                role="alert"
                            >
                                {errorMessage}
                            </motion.p>
                        )}
                    </AnimatePresence>

                    {/* Reassurance */}
                    {status !== "success" && status !== "unsubscribed" && (
                        <p className="text-xs text-text-muted mt-4">
                            {t.newsletter.reassurance}
                        </p>
                    )}
                </FadeIn>
            </div>
        </SectionWrapper>
    );
}
